// CELL-BIO9 — settings panel.
// Overlay triggered by [SETTINGS] button in #sysbar.
// Controls: text speed, CRT toggle, audio toggle, save export/import.
// All preferences stored in localStorage. No new colors or fonts.

import { audioEngine } from "../engine/audio";
import type { Terminal } from "../engine/terminal";
import { getActiveSlot } from "../engine/saveManager";

const LS_SPEED = "cellbio9_speed";
const LS_CRT = "cellbio9_crt";
const LS_MUTE = "cellbio9_mute";

// Stored so refreshPanel (called on every settings interaction) can fire it.
let _onCrtChange: ((crtOn: boolean) => void) | undefined;

export const SPEED_OPTIONS: Array<{ label: string; ms: number }> = [
  { label: "FAST (10ms)", ms: 10 },
  { label: "NORMAL (30ms)", ms: 30 },
  { label: "SLOW (60ms)", ms: 60 },
];

/** Read stored speed preference; returns ms/char (default 30). */
export function loadSpeedPref(): number {
  const stored = localStorage.getItem(LS_SPEED);
  if (stored !== null) {
    const n = Number(stored);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return 30;
}

/** Initialise the settings panel and wire the [SETTINGS] button.
 *  @param onCrtChange  Called with the new CRT state whenever it toggles,
 *                      so main.ts can sync the warp overlay without a
 *                      dynamic import from settings.ts.
 */
export function initSettings(terminal: Terminal, onCrtChange?: (crtOn: boolean) => void): void {
  _onCrtChange = onCrtChange;
  const sysbar = document.getElementById("sysbar");
  if (!sysbar) return;

  // Create the [SETTINGS] button.
  const btn = document.createElement("button");
  btn.id = "settings-btn";
  btn.type = "button";
  btn.title = "Settings";
  btn.textContent = "[SETTINGS]";
  // Insert before the first child (audio toggle is prepended, CRT is last).
  sysbar.insertBefore(btn, sysbar.firstChild);

  // Create the overlay panel (hidden by default).
  const panel = buildPanel(terminal);
  document.body.appendChild(panel);

  btn.addEventListener("click", () => {
    audioEngine.choiceBeep(0);
    const isOpen = panel.classList.contains("open");
    if (isOpen) {
      closePanel(panel);
    } else {
      openPanel(panel, terminal);
    }
  });

  // Close on Escape.
  window.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && panel.classList.contains("open")) {
      closePanel(panel);
    }
  });
}

function openPanel(panel: HTMLElement, terminal: Terminal): void {
  refreshPanel(panel, terminal);
  panel.classList.add("open");
  panel.setAttribute("aria-hidden", "false");
}

function closePanel(panel: HTMLElement): void {
  panel.classList.remove("open");
  panel.setAttribute("aria-hidden", "true");
}

function buildPanel(_terminal: Terminal): HTMLElement {
  const panel = document.createElement("div");
  panel.id = "settings-panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", "Settings");
  panel.setAttribute("aria-hidden", "true");
  // Content is populated on open via refreshPanel.
  return panel;
}

function refreshPanel(panel: HTMLElement, terminal: Terminal): void {
  panel.textContent = "";

  const addLine = (text: string, cls = "system"): HTMLElement => {
    const p = document.createElement("p");
    p.className = `line ${cls}`;
    p.textContent = text;
    panel.appendChild(p);
    return p;
  };

  addLine("// SETTINGS", "system");
  addLine("─────────────────────────────────────────", "muted");

  // ── Text speed ──────────────────────────────────────────────
  addLine("TEXT SPEED", "system");
  const currentMs = loadSpeedPref();
  const speedGroup = document.createElement("div");
  speedGroup.className = "settings-group";
  for (const opt of SPEED_OPTIONS) {
    const btn = makeSettingBtn(
      opt.label,
      opt.ms === currentMs ? "active" : ""
    );
    btn.addEventListener("click", () => {
      terminal.setSpeed(opt.ms);
      localStorage.setItem(LS_SPEED, String(opt.ms));
      audioEngine.choiceBeep(0);
      refreshPanel(panel, terminal);
    });
    speedGroup.appendChild(btn);
  }
  panel.appendChild(speedGroup);

  addLine("─────────────────────────────────────────", "muted");

  // ── CRT toggle ──────────────────────────────────────────────
  const crtOn = document.body.classList.contains("crt");
  const crtBtn = makeSettingBtn(`CRT EFFECTS: ${crtOn ? "ON" : "OFF"}`, crtOn ? "active" : "");
  crtBtn.addEventListener("click", () => {
    document.body.classList.toggle("crt");
    const nowOn = document.body.classList.contains("crt");
    localStorage.setItem(LS_CRT, nowOn ? "on" : "off");
    // Notify caller (main.ts) to sync warp overlay.
    _onCrtChange?.(nowOn);
    audioEngine.choiceBeep(0);
    refreshPanel(panel, terminal);
  });
  panel.appendChild(crtBtn);

  // ── Audio toggle ─────────────────────────────────────────────
  const muted = audioEngine.isMuted();
  const audioBtn = makeSettingBtn(`AUDIO: ${muted ? "OFF" : "ON"}`, muted ? "" : "active");
  audioBtn.addEventListener("click", () => {
    audioEngine.start();
    audioEngine.setMuted(!audioEngine.isMuted());
    localStorage.setItem(LS_MUTE, audioEngine.isMuted() ? "on" : "off");
    audioEngine.choiceBeep(0);
    refreshPanel(panel, terminal);
  });
  panel.appendChild(audioBtn);

  addLine("─────────────────────────────────────────", "muted");

  // ── Save export ──────────────────────────────────────────────
  addLine("SAVE DATA", "system");

  const exportBtn = makeSettingBtn("EXPORT SAVE (COPY TO CLIPBOARD)", "");
  exportBtn.addEventListener("click", () => {
    const slot = getActiveSlot();
    const raw = localStorage.getItem(`cellbio9_slot_${slot}`);
    if (!raw) {
      addLine("No save data in active slot.", "warning");
      return;
    }
    const encoded = btoa(raw);
    navigator.clipboard.writeText(encoded).then(() => {
      audioEngine.choiceBeep(1);
      addLine("Save copied to clipboard.", "system");
    }).catch(() => {
      // Fallback: show in a prompt.
      window.prompt("Copy this save string:", encoded);
    });
  });
  panel.appendChild(exportBtn);

  const importBtn = makeSettingBtn("IMPORT SAVE (PASTE STRING)", "");
  importBtn.addEventListener("click", () => {
    const input = window.prompt("Paste your save string:");
    if (!input) return;
    try {
      const decoded = atob(input.trim());
      const blob = JSON.parse(decoded) as { version: number; state: { currentNode: string } };
      if (!blob.version || !blob.state?.currentNode) throw new Error("Invalid save");
      const slot = getActiveSlot();
      localStorage.setItem(`cellbio9_slot_${slot}`, decoded);
      audioEngine.choiceBeep(1);
      addLine("Save imported. Reload the page to apply.", "system");
    } catch {
      addLine("Invalid save string. Import failed.", "warning");
    }
  });
  panel.appendChild(importBtn);

  addLine("─────────────────────────────────────────", "muted");

  // ── Close button ─────────────────────────────────────────────
  const closeBtn = makeSettingBtn("[ESC] CLOSE", "");
  closeBtn.addEventListener("click", () => {
    audioEngine.choiceBeep(2);
    closePanel(panel);
  });
  panel.appendChild(closeBtn);
}

function makeSettingBtn(label: string, extraClass: string): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = `setting-btn${extraClass ? ` ${extraClass}` : ""}`;
  btn.textContent = label;
  return btn;
}
